import React, { useState, useEffect, useCallback } from 'react';
import { useBooking } from '../context/BookingContext';
import { apiService } from '../services/apiService';

const BookingSidebar = ({ onContinue }) => {
  const { state, dispatch, formatCurrency, formatDisplayDate, calculateNights } = useBooking();

  const nights = calculateNights();
  const totalQtyInCart = state.cart.reduce((acc, item) => acc + item.quantity, 0);
  const requiredRooms = state.searchState?.room_guest?.length || 0;

  const isCheckoutEnabled = totalQtyInCart === requiredRooms && totalQtyInCart > 0;

  const [expandedSections, setExpandedSections] = React.useState({
    bookingDetails: true,
    selectedRooms: true,
    priceBreakdown: true,
    taxDetails: false
  });

  // State for available promos
  const [availablePromos, setAvailablePromos] = useState([]);
  const [selectedPromoCode, setSelectedPromoCode] = useState(null);
  const [appliedPromoCode, setAppliedPromoCode] = useState(state.searchState?.promo_code || null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Helper function to check if any other offers are applied
  const hasOtherOffersApplied = useCallback(() => {
    if (!state.cart || state.cart.length === 0) return false;
    
    return state.cart.some(item => {
      // Check for free night offers
      if (item.isFreeNightOffer) return true;
      if (item.freeNights > 0) return true;
      
      // Check for rate plan offers that are NOT promo codes
      if (item.ratePlan?.offer) {
        const offer = item.ratePlan.offer;
        // If it's a promo code offer, it's allowed
        if (offer.is_promo_code === true) return false;
        // Any other offer type means promo cannot be applied
        return true;
      }
      
      // Check for discount info that's not a promo
      if (item.discountInfo && !item.discountInfo.is_promo_code) {
        return true;
      }
      
      // Check for offer name that's not a promo
      if (item.offerName && !item.offerName.toLowerCase().includes('promo')) {
        return true;
      }
      
      return false;
    });
  }, [state.cart]);

  // Prepare cart data for promo API
  const prepareCartDataForPromo = useCallback(() => {
    if (!state.cart || state.cart.length === 0) return null;
    
    const formatDate = (d) => {
      const date = new Date(d);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };
    
    // CRITICAL FIX: Use the same calculateNights function for consistency
    const totalNights = calculateNights();
    
    // Calculate total guests
    const totalGuests = state.cart.reduce((total, item) => {
      return total + (item.adultsCount || 2) + (item.childrenCount || 0);
    }, 0);
    
    const roomDetails = state.cart.map((item, index) => {
      // Get adults count from multiple possible sources
      let adultsCount = 2;
      let childrenCount = 0;
      let childAges = [];
      
      if (item.guestInfo) {
        adultsCount = item.guestInfo.adults || item.adultsCount || 2;
        childrenCount = item.guestInfo.children || item.childrenCount || 0;
        childAges = item.guestInfo.childAges || [];
      } else if (item.originalGuestConfig) {
        adultsCount = item.originalGuestConfig.adults || 2;
        childrenCount = item.originalGuestConfig.children || 0;
        childAges = item.originalGuestConfig.childAges || [];
      } else if (state.searchState?.room_guest && item.roomNumber) {
        const roomGuest = state.searchState.room_guest[item.roomNumber - 1];
        if (roomGuest) {
          adultsCount = roomGuest.no_of_adults || 2;
          childrenCount = roomGuest.no_of_children || 0;
          childAges = roomGuest.child_ages || [];
        }
      } else {
        adultsCount = item.adultsCount || 2;
        childrenCount = item.childrenCount || 0;
        childAges = item.childAges || [];
      }
      
      // Calculate price per night
      const pricePerNight = item.roomPrice || (item.totalForStay / totalNights);
      const originalPricePerNight = item.originalPrice || pricePerNight;
      
      // Format child ages as array of objects with age property
      const formattedChildAges = Array.isArray(childAges) && childAges.length > 0
        ? childAges.map(age => {
            if (typeof age === 'object' && age !== null) {
              return { age: parseInt(age.age, 10) };
            }
            return { age: parseInt(age, 10) };
          })
        : [];
      
      return {
        room_number: index + 1,
        category_id: item.categoryId || item.roomId,
        category_name: item.roomName,
        rate_plan_id: item.ratePlanId,
        rate_plan_name: item.rateName,
        adults_count: adultsCount,
        children_count: childrenCount,
        child_ages: formattedChildAges,
        room_price: parseFloat(pricePerNight),
        original_price: parseFloat(originalPricePerNight),
        quantity: item.quantity || 1,
        offer_applied: item.offerName || ''
      };
    });
    
    const subtotal = state.subtotal || state.cart.reduce((sum, item) => sum + (item.totalForStay || 0), 0);
    
    return {
      promo_code: selectedPromoCode,
      booking_details: {
        hotel_id: parseInt(state.searchState.hotelId),
        hotel_name: state.searchState.hotel,
        check_in: formatDate(state.searchState.checkin),
        check_out: formatDate(state.searchState.checkout),
        total_nights: totalNights, // Now using the correct calculateNights value
        total_guests: totalGuests,
        total_rooms: state.cart.reduce((total, item) => total + (item.quantity || 1), 0)
      },
      room_details: roomDetails,
      subtotal: parseFloat(subtotal)
    };
  }, [state.cart, state.searchState, state.subtotal, selectedPromoCode, calculateNights]);

  // Fetch available promos from the API response
  const fetchAvailablePromos = useCallback(async () => {
    if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
      return;
    }
    
    try {
      const formatDate = (d) => {
        const date = new Date(d);
        // Use UTC to avoid timezone issues
        return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
      };
      
      const searchParams = {
        startDate: formatDate(state.searchState.checkin),
        endDate: formatDate(state.searchState.checkout),
        rooms: state.rooms.length,
        guests: state.searchState.guestCount,
        roomsData: state.rooms,
      };
      
      const hotelData = await apiService.getHotelData(state.searchState.hotelId, searchParams);
      
      if (hotelData.available_promo_codes && hotelData.available_promo_codes.length > 0) {
        setAvailablePromos(hotelData.available_promo_codes);
      } else {
        setAvailablePromos([]);
      }
    } catch (error) {
      console.error('Error fetching promos:', error);
      setAvailablePromos([]);
    }
  }, [state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout, state.rooms.length, state.searchState.guestCount, state.rooms]);
  // Fetch promos when cart is empty or when dates/hotel change
  useEffect(() => {
    if (state.cart.length === 0 && state.searchState.hotelId) {
      fetchAvailablePromos();
    }
  }, [state.cart.length, state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout, fetchAvailablePromos]);

  // Handle apply promo
  const handleApplyPromo = async (promoCode) => {
    if (!promoCode) return;
    
    // Check if any other offers are applied
    if (hasOtherOffersApplied()) {
      setPromoError('Cannot apply promo code when other offers are already applied to selected rooms.');
      return;
    }
    
    setApplyingPromo(true);
    setPromoError('');
    setPromoSuccess(false);
    
    try {
      const promoData = prepareCartDataForPromo();
      
      if (!promoData) {
        setPromoError('Unable to prepare cart data');
        setApplyingPromo(false);
        return;
      }
      
      promoData.promo_code = promoCode;
      
      console.log('📦 Sending promo data to API:', JSON.stringify(promoData, null, 2));
      
      const promoResponse = await apiService.applyPromoCodeToCart(promoData);
      
      console.log('📦 Cart promo validation response:', promoResponse);
      
      if (!promoResponse.success) {
        setPromoError(promoResponse.message || 'Invalid promo code');
        setApplyingPromo(false);
        return;
      }
      
      const responseData = promoResponse.data;
      const discountAmount = responseData?.savings_summary?.total_savings || 0;
      const discountedSubtotal = responseData?.discounted_charges?.subtotal || 0;
      const discountedTaxes = responseData?.discounted_charges?.taxes?.total_tax || 0;
      const discountedTotal = responseData?.discounted_charges?.total || 0;
      const roomDetailsFromResponse = responseData?.room_details || [];
      
      if (discountAmount === 0) {
        setPromoError('Promo code could not be applied to this booking.');
        setApplyingPromo(false);
        return;
      }
      
      // Update cart items with discounted prices from API response
      const updatedCart = state.cart.map((item, index) => {
        const roomDetail = roomDetailsFromResponse.find(r => r.room_number === index + 1);
        
        if (roomDetail) {
          return {
            ...item,
            discountedPricePerNight: roomDetail.discounted_price_per_night,
            originalPrice: roomDetail.original_price_per_night || item.roomPrice,
            discountAmount: roomDetail.discount_amount || 0,
          };
        }
        
        return item;
      });
      
      // Update state
      setAppliedPromoCode(promoCode);
      setSelectedPromoCode(promoCode);
      setPromoSuccess(true);
      setPromoError('');
      
      // Update search state
      dispatch({
        type: 'UPDATE_SEARCH_STATE',
        payload: { 
          promo_code: promoCode,
          promo_from_widget: false
        },
      });
      
      // Apply the promo discount to cart
      dispatch({
        type: 'APPLY_MANUAL_PROMO',
        payload: {
          discountAmount: discountAmount,
          promoCode: promoCode,
          discountedSubtotal: discountedSubtotal,
          discountedTaxes: discountedTaxes,
          discountedTotal: discountedTotal,
          finalTotal: discountedTotal,
          promoCodeInfo: {
            ...responseData?.promo_code_info,
            id: responseData?.room_details?.[0]?.offer?.id,
            room_details: responseData?.room_details,
            discounted_charges: responseData?.discounted_charges,
            savings_summary: responseData?.savings_summary
          },
          updatedCart: updatedCart,
          fromWidget: false
        }
      });
      
      console.log('✅ Promo applied successfully, discount amount:', discountAmount);
      
      // Refresh available promos after applying
      setTimeout(() => {
        fetchAvailablePromos();
      }, 500);
      
    } catch (error) {
      console.error('Promo validation error:', error);
      setPromoError('Could not validate code. Please try again.');
    } finally {
      setApplyingPromo(false);
    }
  };

  // Handle clear promo
  const handleClearPromo = () => {
    dispatch({ type: 'CLEAR_CART' });
    dispatch({ type: 'CLEAR_DISCOUNT' });
    dispatch({ type: 'UPDATE_SEARCH_STATE', payload: { promo_code: '', promo_from_widget: false } });
    setSelectedPromoCode(null);
    setAppliedPromoCode(null);
    setPromoSuccess(false);
    setPromoError('');
  };

  // Helper function to format discount value
  const formatDiscountValue = (discount) => {
    if (discount.discount_type === 'percentage') {
      return `${discount.discount_value}% OFF`;
    } else if (discount.discount_type === 'fixed') {
      return `${formatCurrency(parseFloat(discount.discount_value))} OFF`;
    }
    return '';
  };

  // Check if a promo is disabled
  const isPromoDisabled = (promoCode) => {
    if (hasOtherOffersApplied()) return true;
    if (appliedPromoCode === promoCode) return true;
    return false;
  };

  const calculateRoomPricingForStay = (cartItem) => {
    const nights = calculateNights();
    let totalForStay = 0;
    let totalOriginalForStay = 0;
    let taxAmount = 0;
    
    const isFreeNightOffer = cartItem.ratePlan?.offer?.discount_form === 'free_nights';
    const freeNights = cartItem.ratePlan?.offer?.no_free_nights || 0;
    const minStayNights = cartItem.ratePlan?.offer?.min_stay_nights || 1;
    
    if (isFreeNightOffer && nights >= minStayNights && freeNights > 0) {
      totalForStay = cartItem.roomPrice * nights;
      totalOriginalForStay = (cartItem.originalPrice || cartItem.roomPrice) * nights;
    } else {
      totalForStay = cartItem.roomPrice * nights;
      totalOriginalForStay = (cartItem.originalPrice || cartItem.roomPrice) * nights;
    }
    
    if (cartItem.taxData) {
      taxAmount = (cartItem.taxData.total_cgst_amount_after_tax || 0) +
                 (cartItem.taxData.total_sgst_amount_after_tax || 0) +
                 (cartItem.taxData.total_cess_amount || 0);
    } else {
      taxAmount = totalForStay * 0.18;
    }
    
    return {
      totalForStay,
      totalOriginalForStay,
      taxAmount,
      isFreeNightOffer,
      freeNights,
      payableNights: Math.max(nights - freeNights, 0),
      hasDiscount: cartItem.hasDiscount || (cartItem.originalPrice > cartItem.roomPrice),
      discountAmount: (cartItem.originalPrice - cartItem.roomPrice) * nights,
      roomPricePerNight: cartItem.roomPrice,
      originalPricePerNight: cartItem.originalPrice || cartItem.roomPrice
    };
  };

  // Main calculation function for display totals

const calculateDisplayTotals = () => {
  const nights = calculateNights();
  
  if (state.cart.length === 0) {
    return {
      subtotal: 0,
      originalSubtotal: 0,
      taxes: 0,
      total: 0,
      finalTotal: 0,
      cgst: 0,
      sgst: 0,
      cess: 0,
      nights: nights,
      totalDiscount: 0,
      freeNightsSavings: 0
    };
  }
  
  // ONLY apply promo discount when:
  // 1. Cart has items
  // 2. hasDiscount is true
  // 3. NOT from widget (fromWidget === false)
  // 4. has discountedSubtotal
  const hasCartPromo = state.cart.length > 0 && 
                       state.hasDiscount && 
                       !state.fromWidget && 
                       state.discountedSubtotal;
  
  if (hasCartPromo) {
    return {
      subtotal: state.discountedSubtotal,
      originalSubtotal: state.originalSubtotal || state.subtotal,
      taxes: state.discountedTaxes,
      total: state.discountedTotal,
      finalTotal: state.discountedTotal,
      cgst: state.discountedTaxes / 2,
      sgst: state.discountedTaxes / 2,
      cess: 0,
      nights: nights,
      totalDiscount: state.discountAmount,
      freeNightsSavings: 0,
      hasCartPromo: true,
      promoCode: state.promoCode
    };
  }
  
  let totalSubtotal = 0;
  let totalOriginalSubtotal = 0;
  let totalTaxes = 0;
  let totalCGST = 0;
  let totalSGST = 0;
  let totalCess = 0;
  let totalDiscount = 0;
  let freeNightsSavings = 0;

  state.cart.forEach((item) => {
    // Use the actual room price from the cart item
    const roomPrice = item.discountedPricePerNight || item.roomPrice;
    const originalPrice = item.originalPrice || roomPrice;
    
    const roomPricing = calculateRoomPricingForStay({
      ...item,
      roomPrice: roomPrice,
      originalPrice: originalPrice
    });
    
    totalSubtotal += roomPricing.totalForStay;
    totalOriginalSubtotal += roomPricing.totalOriginalForStay;
    totalTaxes += roomPricing.taxAmount;
    
    if (item.taxData) {
      totalCGST += item.taxData.total_cgst_amount_after_tax || 0;
      totalSGST += item.taxData.total_sgst_amount_after_tax || 0;
      totalCess += item.taxData.total_cess_amount || 0;
    }
    
    if (roomPricing.hasDiscount) {
      totalDiscount += roomPricing.discountAmount;
    }
    
    if (roomPricing.isFreeNightOffer && roomPricing.freeNights > 0) {
      const savingsFromFreeNights = roomPricing.originalPricePerNight * roomPricing.freeNights;
      freeNightsSavings += savingsFromFreeNights;
    }
  });
  
  const total = totalSubtotal + totalTaxes;
  
  return {
    subtotal: totalSubtotal,
    originalSubtotal: totalOriginalSubtotal,
    taxes: totalTaxes,
    total: total,
    finalTotal: Math.round(total),
    cgst: totalCGST,
    sgst: totalSGST,
    cess: totalCess,
    nights: nights,
    totalDiscount: totalDiscount,
    freeNightsSavings: freeNightsSavings,
    hasDiscount: totalDiscount > 0,
    hasFreeNights: freeNightsSavings > 0
  };
};

  const displayTotals = calculateDisplayTotals();

  // Get room display info
  // Get room display info - UPDATED to show discounted prices when promo is applied
const getRoomDisplayInfo = () => {
  const roomInfo = [];
  const nights = calculateNights();
  
  // Check if promo is applied
  const hasCartPromo = state.cart.length > 0 && 
                       state.hasDiscount && 
                       !state.fromWidget && 
                       state.discountedSubtotal;

  state.cart.forEach((item, index) => {
    // Determine which prices to use (original or promo-discounted)
    let effectiveRoomPrice = item.roomPrice;
    let effectiveOriginalPrice = item.originalPrice || item.roomPrice;
    
    // If promo is applied and we have discounted info from the API
    if (hasCartPromo && item.discountedPricePerNight) {
      effectiveRoomPrice = item.discountedPricePerNight;
      effectiveOriginalPrice = item.originalPrice || item.roomPrice;
    }
    
    const roomPricing = calculateRoomPricingForStay({
      ...item,
      roomPrice: effectiveRoomPrice,
      originalPrice: effectiveOriginalPrice
    });
    
    let adultsCount = 2;
    let childrenCount = 0;
    
    if (item.guestInfo) {
      adultsCount = item.guestInfo.adults || item.adultsCount || 2;
      childrenCount = item.guestInfo.children || item.childrenCount || 0;
    } else if (item.originalGuestConfig) {
      adultsCount = item.originalGuestConfig.adults || 2;
      childrenCount = item.originalGuestConfig.children || 0;
    } else if (state.searchState?.room_guest) {
      const roomGuest = state.searchState.room_guest[item.roomNumber - 1];
      if (roomGuest) {
        adultsCount = roomGuest.no_of_adults || 2;
        childrenCount = roomGuest.no_of_children || 0;
      } else {
        adultsCount = item.adultsCount || 2;
        childrenCount = item.childrenCount || 0;
      }
    } else {
      adultsCount = item.adultsCount || 2;
      childrenCount = item.childrenCount || 0;
    }
    
    roomInfo.push({
      roomNumber: index + 1,
      roomName: item.roomName,
      rateName: item.rateName,
      roomPricePerNight: effectiveRoomPrice,
      originalPricePerNight: effectiveOriginalPrice,
      totalForStay: roomPricing.totalForStay,
      taxForStay: roomPricing.taxAmount,
      isFreeNightOffer: roomPricing.isFreeNightOffer,
      freeNights: roomPricing.freeNights,
      payableNights: roomPricing.payableNights,
      hasDiscount: roomPricing.hasDiscount,
      discountAmount: roomPricing.discountAmount,
      adultsCount: adultsCount,
      childrenCount: childrenCount,
      taxData: item.taxData,
      ratePlan: item.ratePlan,
      guestInfo: item.guestInfo,
      // Add flag to show if promo discount is applied to this room
      hasPromoDiscount: hasCartPromo && item.discountedPricePerNight > 0
    });
  });

  return roomInfo;
};
  const roomDisplayInfo = getRoomDisplayInfo();

  const getGroupedRoomInfo = () => {
    const groups = {};
    const nights = calculateNights();

    if (state.cart.length === 0) {
      return [];
    }

    const roomGuestInfo = state.searchState.room_guest || [];

    state.cart.forEach((item, cartIndex) => {
      if (!groups[item.roomName]) {
        groups[item.roomName] = {
          name: item.roomName,
          count: 0,
          adults: 0,
          children: 0,
          totalBasePrice: 0,
          totalDiscountedPrice: 0
        };
      }

      groups[item.roomName].count += item.quantity;
      
      let adultsToAdd = 0;
      let childrenToAdd = 0;
      
      if (item.guestInfo) {
        adultsToAdd = (item.guestInfo.adults || 0) * item.quantity;
        childrenToAdd = (item.guestInfo.children || 0) * item.quantity;
      } else if (item.originalGuestConfig) {
        adultsToAdd = (item.originalGuestConfig.adults || 0) * item.quantity;
        childrenToAdd = (item.originalGuestConfig.children || 0) * item.quantity;
      } else {
        const roomNumber = item.roomNumber;
        if (roomNumber && roomGuestInfo[roomNumber - 1]) {
          const guestInfo = roomGuestInfo[roomNumber - 1];
          adultsToAdd = (guestInfo.no_of_adults || 0) * item.quantity;
          childrenToAdd = (guestInfo.no_of_children || 0) * item.quantity;
        } else {
          adultsToAdd = (item.adultsCount || 0) * item.quantity;
          childrenToAdd = (item.childrenCount || 0) * item.quantity;
        }
      }
      
      groups[item.roomName].adults += adultsToAdd;
      groups[item.roomName].children += childrenToAdd;

      const roomPricing = calculateRoomPricingForStay(item);
      groups[item.roomName].totalBasePrice += roomPricing.totalOriginalForStay;
      groups[item.roomName].totalDiscountedPrice += roomPricing.totalForStay;
    });

    return Object.values(groups);
  };

  const groupedRooms = getGroupedRoomInfo();
  const [isBreakdownOpen, setIsBreakdownOpen] = React.useState(false);

  return (
    <div className="lg:sticky top-24">
      <div className="bg-white shadow-lg lg:shadow-md rounded-xl lg:rounded-lg p-5 lg:p-4 border border-gray-100">
        <div className="space-y-4 lg:space-y-3">
          {/* Stay Details Section */}
          <div className="pb-4 lg:pb-3 border-b border-gray-200">
            <button
              onClick={() => toggleSection('bookingDetails')}
              className="flex items-center justify-between w-full text-left lg:cursor-default mb-2"
            >
              <h4 className="text-xs font-semibold text-text-primary uppercase tracking">Stay Details</h4>
              <svg
                className={`lg:hidden w-4 h-4 text-text-secondary transition-transform duration-200 ${expandedSections.bookingDetails ? 'rotate-180' : ''
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div
              className={`
                lg:block overflow-hidden transition-all duration-300 ease-in-out
                ${expandedSections.bookingDetails ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
              `}
            >
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-text-secondary tracking-tight">Check-in</span>
                    <span className="text-sm font-bold text-text-primary">
                      {formatDisplayDate(state.searchState.checkin)}
                    </span>
                    <span className="text-[11px] text-text-secondary">
                      {new Date(state.searchState.checkin).toLocaleDateString('en-US', { weekday: 'long' })} • 02:00 PM
                    </span>
                  </div>

                  <div className="self-center px-2 py-1 bg-gray-50 border border-gray-100 rounded text-[11px] font-bold text-brand-primary">
                    {nights} {nights === 1 ? 'Night' : 'Nights'}
                  </div>

                  <div className="flex flex-col text-right">
                    <span className="text-[10px] uppercase font-bold text-text-secondary tracking-tight">Check-out</span>
                    <span className="text-sm font-bold text-text-primary">
                      {formatDisplayDate(state.searchState.checkout)}
                    </span>
                    <span className="text-[11px] text-text-secondary">
                      {new Date(state.searchState.checkout).toLocaleDateString('en-US', { weekday: 'long' })} • 11:00 AM
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Rooms Section */}
          <div className="pb-3 border-b border-gray-100">
            <div className="flex justify-between items-end mb-2">
              <h4 className="text-xs font-bold text-text-primary uppercase tracking">
                Guest & Rooms
              </h4>
              {state.cart.length > 0 && (
                <div className="text-right leading-none">
                  <span className="text-sm font-semibold text-text-primary">
                    {totalQtyInCart} Rooms
                  </span>
                  <span className="mx-1 text-gray-300">•</span>
                  <span className="text-sm font-semibold text-text-primary">
                    {groupedRooms.reduce((acc, g) => acc + g.adults, 0)} Adults, {groupedRooms.reduce((acc, g) => acc + g.children, 0)} Children
                  </span>
                </div>
              )}
            </div>

            <div className={`lg:block overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.selectedRooms ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
              <div className="space-y-1">
                {groupedRooms.map((group, idx) => (
                  <div key={idx} className="flex justify-between items-center py-1 group">
                    <h5 className="font-medium text-sm text-text-primary truncate pr-4">
                      {group.count} × {group.name}
                    </h5>
                    <div className="text-[13px] font-medium text-text-secondary whitespace-nowrap flex-shrink-0">
                      {group.adults} Adults, {group.children} Children
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Price Summary Section */}
          <div className="border-gray-100">
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-text-primary uppercase tracking">
                Price
              </h4>

              {/* Free Nights Offer Badge */}
              {displayTotals.hasFreeNights && (
                <div className="mb-2 p-2 bg-green-50 border border-green-100 rounded-md">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-xs font-bold text-green-700">
                        FREE NIGHT OFFER APPLIED!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Promo Code Applied Section */}
              {(state.cart.length > 0 && state.hasDiscount && !state.fromWidget && state.promoCode && state.discountedSubtotal) && (
                <div className="pb-3 border-b border-gray-100">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-bold text-green-700 text-sm">
                            Promo Code Applied
                          </span>
                        </div>
                        <p className="text-xs text-green-600 mt-1">
                          Code: <span className="font-mono font-bold">{state.promoCode}</span>
                        </p>
                        <p className="text-xs text-green-600">
                          You saved {formatCurrency(state.discountAmount)}
                        </p>
                      </div>
                      <button
                        onClick={handleClearPromo}
                        className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Tariff Row */}
              <div className="flex justify-between text-sm">
                <h5 className="text-text-primary font-medium">
                  Tariff for {nights} night{nights !== 1 ? 's' : ''}
                </h5>
                <span className="text-text-primary">
                  {formatCurrency(displayTotals.subtotal)}
                </span>
              </div>

              {/* Taxes Row */}
              <div className="flex justify-between text-sm">
                <h5 className="text-text-primary font-medium">Taxes & Fees</h5>
                <span className="text-text-primary">
                  {formatCurrency(displayTotals.taxes)}
                </span>
              </div>

              {/* Total Payable Row */}
              <div className="flex justify-between items-start pt-3 mt-1 border-t border-gray-100">
                <span className="text-sm font-bold text-text-primary uppercase tracking-tight mt-1">
                  Total Payable
                </span>
                <div className="text-right">
                  <span className="text-xl font-bold text-text-primary block leading-none">
                    {formatCurrency(displayTotals.finalTotal)}
                  </span>
                  
                  {state.cart.length > 0 && (
                    <button
                      onClick={() => setIsBreakdownOpen(true)}
                      className="text-[11px] font-bold text-brand-primary underline mt-1 hover:text-brand-primary-hover transition-colors"
                    >
                      View Price Breakup
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* AVAILABLE PROMOS SECTION - Only show when cart is NOT empty */}
          {state.cart.length > 0 && availablePromos.length > 0 && !hasOtherOffersApplied() && !state.hasDiscount && (
  <div className="border-t border-gray-200 pt-4 mt-2">
    <h4 className="text-xs font-bold text-text-primary uppercase tracking mb-3">
      Available Offers
    </h4>
    <div className="space-y-2">
      {availablePromos.map((promo) => {
        const isDisabled = isPromoDisabled(promo.code);
        const isSelected = selectedPromoCode === promo.code;
        const isApplied = appliedPromoCode === promo.code;
        
        // Get the first offer for display (if multiple, show first)
        const primaryOffer = promo.category_offers?.[0];
        const discountText = primaryOffer 
          ? primaryOffer.discount_type === 'percentage' 
            ? `${primaryOffer.discount_value}% OFF`
            : `${formatCurrency(parseFloat(primaryOffer.discount_value))} OFF`
          : 'Special Offer';
        
        // Check if there are multiple offers
        const hasMultipleOffers = promo.category_offers && promo.category_offers.length > 1;
        
        return (
          <div
            key={promo.code}
            className={`border rounded-lg p-3 transition-all ${
              isApplied
                ? 'border-green-400 bg-green-50'
                : isSelected
                ? 'border-brand-accent bg-brand-accent/5'
                : 'border-gray-200 hover:border-brand-accent/50'
            } ${isDisabled && !isApplied ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer'}`}
          >
            <div className="flex items-start gap-3">
              {/* Radio Button / Checkmark */}
              <div className="flex-shrink-0 pt-1">
                {isApplied ? (
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <input
                    type="radio"
                    name="promoCode"
                    value={promo.code}
                    checked={isSelected}
                    onChange={() => !isDisabled && setSelectedPromoCode(promo.code)}
                    disabled={isDisabled}
                    className="w-4 h-4 text-brand-accent focus:ring-brand-accent border-gray-300"
                  />
                )}
              </div>
              
              {/* Promo Details */}
              <div className="flex-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex-1">
                    {/* Promo Code Badge */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-sm bg-gray-100 px-2 py-0.5 rounded text-text-primary">
                        {promo.code}
                      </span>
                      <span className="font-bold text-sm text-brand-accent">
                        {discountText}
                      </span>
                    </div>
                    
                    {/* Description or Common Text */}
                    {promo.description ? (
                      <p className="text-xs text-text-secondary mt-1.5 leading-relaxed">
                        {promo.description}
                      </p>
                    ) : (
                      <p className="text-xs text-text-secondary mt-1.5 leading-relaxed">
                        Get {discountText.toLowerCase()} on your booking with code {promo.code}
                      </p>
                    )}
                    
                    {/* Multiple Offers Indicator */}
                    {hasMultipleOffers && (
                      <div className="flex items-center gap-1 mt-1 hidden">
                        <svg className="w-3 h-3 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-[10px] text-text-tertiary">
                          Applicable on {promo.category_offers.length} room type{promo.category_offers.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Apply/Select Button */}
                  {!isApplied && (
                    <button
                      onClick={() => !isDisabled && handleApplyPromo(promo.code)}
                      disabled={isDisabled || applyingPromo}
                      className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                        isSelected
                          ? 'bg-brand-accent text-white hover:bg-brand-accent-hover'
                          : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
                      } ${(isDisabled || applyingPromo) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {applyingPromo && selectedPromoCode === promo.code ? (
                        <div className="flex items-center gap-1">
                          <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Applying...
                        </div>
                      ) : isSelected ? 'Apply Now' : 'Select'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
    
    {/* Error Message */}
    {promoError && (
      <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-xs text-red-600 flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {promoError}
        </p>
      </div>
    )}
    
    {/* Success Message */}
    {promoSuccess && (
      <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-xs text-green-600 flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Promo code applied successfully!
        </p>
      </div>
    )}
  </div>
)}

          {/* Message when other offers are applied (promos disabled) */}
          {state.cart.length > 0 && availablePromos.length > 0 && hasOtherOffersApplied() && !state.hasDiscount && (
            <div className="border-t border-gray-200 pt-4 mt-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-xs text-amber-700">
                    Promo codes cannot be combined with existing offers on selected rooms.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Checkout Section */}
          <div className="mt-6">
            {state.cart.length > 0 && totalQtyInCart !== requiredRooms && (
              <div className="mb-3 text-center">
                <p className="text-[12px] font-bold text-red-600 uppercase tracking-wide">
                  {totalQtyInCart < requiredRooms
                    ? `Please Add ${requiredRooms - totalQtyInCart} More Room${requiredRooms - totalQtyInCart > 1 ? 's' : ' to continue'}`
                    : `Limit Exceeded: Remove ${totalQtyInCart - requiredRooms} Room${totalQtyInCart - requiredRooms > 1 ? 's' : ''}`
                  }
                </p>
              </div>
            )}

            <button
              onClick={onContinue}
              disabled={!isCheckoutEnabled}
              className={`w-full bg-brand-accent text-text-dark-on-accent px-6 py-3.5 rounded-lg font-semibold text-base hover:bg-brand-accent-hover transition-all duration-200 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent shadow-md ${!isCheckoutEnabled ? 'opacity-50 cursor-not-allowed grayscale-[0.5]' : ''
                }`}
            >
              Continue to Checkout
            </button>
          </div>

          {/* Breakdown Modal */}
          {isBreakdownOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
              <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                  <h3 className="font-bold text-base text-text-primary">Price Breakup Detail</h3>
                  <button
                    onClick={() => setIsBreakdownOpen(false)}
                    className="p-1.5 hover:bg-gray-200 rounded-full transition-colors text-text-secondary"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="p-5 max-h-[60vh] overflow-y-auto">
                  <div className="space-y-4">
                    {/* Room-wise List */}
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold text-text-secondary uppercase tracking-[0.15em] mb-2">
                        Selected Accommodations
                      </h4>
                      {roomDisplayInfo.map((room, index) => (
                        <div key={index} className="py-3 border-b border-gray-100 last:border-0">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold bg-gray-100 text-text-secondary px-1.5 py-0.5 rounded">
                                  Room {room.roomNumber}
                                </span>
                                <h5 className="font-bold text-sm text-text-primary">{room.roomName}</h5>
                              </div>
                              <p className="text-[11px] text-brand-primary font-semibold mt-1 pl-8">
                                {room.rateName}
                              </p>
                              <p className="text-[11px] text-text-secondary pl-8">
                                {room.adultsCount} Adults, {room.childrenCount} Children
                              </p>
                              {/* Show promo discount badge if applicable */}
                              {room.hasPromoDiscount && (
                                <div className="mt-1 pl-8">
                                  <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">
                                    Promo Discount Applied
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              {room.hasPromoDiscount && room.originalPricePerNight > room.roomPricePerNight && (
                                <div className="text-[10px] text-text-secondary line-through mb-0.5">
                                  {formatCurrency(room.originalPricePerNight * nights)}
                                </div>
                              )}
                              <span className="font-bold text-sm text-text-primary">
                                {formatCurrency(room.totalForStay)}
                              </span>
                              {room.taxForStay > 0 && (
                                <p className="text-[10px] text-text-secondary mt-0.5">
                                  Tax: {formatCurrency(room.taxForStay)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Financial Summary */}
                    <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
                      <div className="space-y-2">
                        {/* Taxes */}
                        <div className="flex justify-between text-sm text-text-secondary">
                          <span>Taxes & Fees:</span>
                          <span className="font-medium text-text-primary">
                            {formatCurrency(displayTotals.taxes)}
                          </span>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-3 mt-1 border-t-2 border-gray-100">
                        <span className="text-base font-bold text-text-primary">Grand Total</span>
                        <span className="text-xl font-bold text-text-primary">
                          {formatCurrency(displayTotals.finalTotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 pt-2">
                  <button
                    onClick={() => setIsBreakdownOpen(false)}
                    className="w-full py-3 bg-text-primary text-white rounded-xl font-bold shadow-md active:scale-[0.98] transition-all"
                  >
                    Close Detail View
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingSidebar;